import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'components/Card'
import { observer } from 'mobx-react'
import { Id } from 'types/definitions'
import JobEvent from './JobEvent'
import JobViewModel from '../JobViewModel'
import { SectionError, SectionLoading } from '../Section/Section'
import Icon from 'components/Icon'
import DetailsViewModel, { EventFilter } from '../DetailsView/DetailsViewModel'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import { SectionHeaderAction } from 'components/Section'
import { match } from 'utils/pattern-match-util'
import InfiniteScrollLoader from '../../../../../components/InfiniteScrollLoader'

interface Props {
  vm: JobViewModel
}

export default observer(function EventsCard({ vm }: Props) {
  return (
    <Card dataTest="events-new">
      <CardHeader>
        <CardTitle>
          All events{' '}
          <EventsFilters
            detailsVM={vm.detailsVM}
            icon={<Icon name="dropdown" size={20}></Icon>}
            isCardLayout
          />
        </CardTitle>
      </CardHeader>
      {vm.detailsVM.jobEventsScrollProvider.fetch.match({
        pending: () => (
          <CardContent>
            <SectionLoading />
          </CardContent>
        ),
        rejected: (err) => (
          <CardContent>
            <SectionError
              title="An error occurred while loading events."
              error={err}
            />
          </CardContent>
        ),
        resolved: () => (
          <CardContent scroll listContent>
            <>
              {vm.detailsVM.filteredEvents.map((event) => (
                <JobEvent event={event} key={Id.assert(event.id)} />
              ))}

              <InfiniteScrollLoader
                compact
                scrollProvider={vm.detailsVM.jobEventsScrollProvider}
              />
            </>
          </CardContent>
        ),
      })}
    </Card>
  )
})

interface EventsFiltersProps {
  detailsVM: DetailsViewModel
  icon: React.ReactNode
  isCardLayout?: boolean
}

export const EventsFilters = observer(
  ({ detailsVM, icon, isCardLayout }: EventsFiltersProps) => {
    const suffix = isCardLayout ? 'new' : 'classic'

    return (
      <Dropdown
        autoDismiss
        position="bottom"
        dataTest={`job-events-filters-${suffix}`}
        minWidth={200}
        button={
          <SectionHeaderAction
            compact
            title={getEventFilterText(detailsVM.eventFilter)}
          >
            {icon}
          </SectionHeaderAction>
        }
      >
        <DropdownTitle>Event type</DropdownTitle>
        <DropdownItem
          active={detailsVM.eventFilter === EventFilter.All}
          checkmark={detailsVM.eventFilter === EventFilter.All}
          onClick={() => detailsVM.setEventFilter(EventFilter.All)}
          data-test={`job-events-filter-all-${suffix}`}
        >
          {getEventFilterText(EventFilter.All)}
        </DropdownItem>

        <DropdownItem
          active={detailsVM.eventFilter === EventFilter.JobUpdates}
          checkmark={detailsVM.eventFilter === EventFilter.JobUpdates}
          onClick={() => detailsVM.setEventFilter(EventFilter.JobUpdates)}
          data-test={`job-events-filter-job-updates-${suffix}`}
        >
          {getEventFilterText(EventFilter.JobUpdates)}
        </DropdownItem>

        <DropdownItem
          active={detailsVM.eventFilter === EventFilter.TaskUpdates}
          checkmark={detailsVM.eventFilter === EventFilter.TaskUpdates}
          onClick={() => detailsVM.setEventFilter(EventFilter.TaskUpdates)}
          data-test={`job-events-filter-task-updates-${suffix}`}
        >
          {getEventFilterText(EventFilter.TaskUpdates)}
        </DropdownItem>
      </Dropdown>
    )
  },
)

const getEventFilterText = (filter: EventFilter) =>
  match(filter, {
    [EventFilter.All]: () => 'All updates',
    [EventFilter.JobUpdates]: () => 'Job updates',
    [EventFilter.TaskUpdates]: () => 'Task updates',
  })
